/// <amd-module name="Test/AttachmentsView/Store/AttachmentsViewStore" />
import { AttachmentsViewActionsHub } from "Test/AttachmentsView/Actions/AttachmentsViewActionsHub";
import { ISelectedAttachment } from "Test/AttachmentsView/Attachment";
import { TestAttachmentModel } from "Test/AttachmentsView/TestAttachmentModel";
import { Store } from "VSS/Reflux/Store";
import { IListSelection, ListSelection } from "VSSUI/List";

export interface IAttachmentsViewState {
    errorMessage: string | null;
    errorMessageInPreview: string | null;
    attachments: TestAttachmentModel | null;
    content: string | null;
    selection: IListSelection;
    runId: number,
    resultId: number,
    subresultId: number,
    isAttachmentsListLoading: boolean;
    isLogStoreAttachmentsListLoading: boolean;
    isAttachmentPreviewLoading: boolean;
    setKey: string | null;
    hasPublishTestResultsPermission: boolean;
    shouldInvokeFirstItem: boolean;
    lastSelectedAttachment: ISelectedAttachment | null;
    isAttachmentPreviewable: boolean;
    isOnlyAttachmentPreviewMode: boolean;
}

export class AttachmentsViewStore extends Store {

    private _state: IAttachmentsViewState;

    constructor(private _actionsHub: AttachmentsViewActionsHub) {
        super("");
        this._initialize();
    }

    public getState(): IAttachmentsViewState {
        return this._state;
    }

    private _initialize(): void {
        this._state = this._getDefaultState();

        this._actionsHub.setDefaultState.addListener(this._setDefaultState);
        this._actionsHub.onError.addListener(this._onErrorListener);
        this._actionsHub.onErrorInPreview.addListener(this._onErrorInPreviewListener);
        this._actionsHub.attachmentsLoaded.addListener(this._attachmentsLoadedListener);
        this._actionsHub.showAttachmentPreview.addListener(this._showAttachmentPreview);
        this._actionsHub.setLastSelectedAttachment.addListener(this._setLastSelectedAttachment);
        this._actionsHub.setAttachmentContentLoading.addListener(this._setAttachmentContentLoading);
        this._actionsHub.closeErrorMessage.addListener(this._closeErrorMessage);
        this._actionsHub.setLastSelectedAttachmentUnpreviewable.addListener(this._setLastSelectedAttachmentUnpreviewable);
        this._actionsHub.updateCurrentAttachmentPreviewMode.addListener(this._updateCurrentAttachmentPreviewModeListener);
    }

    public dispose() {
        this._actionsHub.setDefaultState.removeListener(this._setDefaultState);
        this._actionsHub.onError.removeListener(this._onErrorListener);
        this._actionsHub.onErrorInPreview.removeListener(this._onErrorInPreviewListener);
        this._actionsHub.attachmentsLoaded.removeListener(this._attachmentsLoadedListener);
        this._actionsHub.showAttachmentPreview.removeListener(this._showAttachmentPreview);
        this._actionsHub.setLastSelectedAttachment.removeListener(this._setLastSelectedAttachment);
        this._actionsHub.setAttachmentContentLoading.removeListener(this._setAttachmentContentLoading);
        this._actionsHub.closeErrorMessage.removeListener(this._closeErrorMessage);
        this._actionsHub.setLastSelectedAttachmentUnpreviewable.removeListener(this._setLastSelectedAttachmentUnpreviewable);
        this._actionsHub.updateCurrentAttachmentPreviewMode.removeListener(this._updateCurrentAttachmentPreviewModeListener);
    }

    private _getDefaultState(): IAttachmentsViewState {
        return {
            content: null,
            errorMessageInPreview: null,
            errorMessage: null,
            attachments: null,
            selection: new ListSelection(false),
            runId: 0,
            resultId: 0,
            subresultId: 0,
            isAttachmentsListLoading: true,
            isLogStoreAttachmentsListLoading: true,
            isAttachmentPreviewLoading: false,
            setKey: "set",
            hasPublishTestResultsPermission: false,
            shouldInvokeFirstItem: false,
            lastSelectedAttachment: null,
            isAttachmentPreviewable: true,
            isOnlyAttachmentPreviewMode: false //By default show only List of attachments
        };
    }

    private _setDefaultState = (): void => {
        this._state = this._getDefaultState();
        this.emitChanged();
    }

    private _onErrorListener = (errorMessage: string): void => {
        this._state.errorMessage = errorMessage;
        this._state.isAttachmentsListLoading = false;
        this._state.isLogStoreAttachmentsListLoading = false;
        this.emitChanged();
    }

    private _onErrorInPreviewListener = (errorMessage: string): void => {
        this._state.errorMessageInPreview = errorMessage;
        this._state.isAttachmentPreviewLoading = false;
        this._state.content = null;
        this.emitChanged();
    }

    private _closeErrorMessage = (): void => {
        this._state.errorMessage = null;
        this._state.errorMessageInPreview = null;
        this.emitChanged();
    }

    private _setLastSelectedAttachmentUnpreviewable = () => {
        this._state.isAttachmentPreviewable = false;
        this._state.content = null;
        this.emitChanged();
    }

    private _attachmentsLoadedListener = (attachments: TestAttachmentModel): void => {
        this._state.attachments = attachments;
        this._state.isAttachmentsListLoading = false;
        this._state.isLogStoreAttachmentsListLoading = false;
        this._state.isAttachmentPreviewLoading = true;
        let legacyAttachments = attachments.getLegacyAttachments();
        let logStoreAttachments = attachments.getLogStoreAttachments();
        if ((legacyAttachments && legacyAttachments.length > 0) ||
            (logStoreAttachments && logStoreAttachments.length > 0)) {
            this._state.selection.select(0);
            this._state.isAttachmentPreviewLoading = false;
            this._state.shouldInvokeFirstItem = true;
        }
        this.emitChanged();
    }

    private _showAttachmentPreview = (attachmentContent: string): void => {
        this._state.content = attachmentContent;
        this._state.isAttachmentPreviewLoading = false;
        this._state.errorMessageInPreview = null;
        this._state.isAttachmentPreviewable = true;

        this.emitChanged();
    }

    private _setLastSelectedAttachment = (selectedAttachment: ISelectedAttachment): void => {
        this._state.lastSelectedAttachment = selectedAttachment;
        this._state.isAttachmentPreviewLoading = false;
        this._state.shouldInvokeFirstItem = false;
        this._state.isAttachmentPreviewable = true;
        this._state.content = null;
        this._state.errorMessageInPreview = null;

        this.emitChanged();
    }

    private _setAttachmentContentLoading = (): void => {
        this._state.isAttachmentPreviewLoading = true;
        this._state.content = null;
        this.emitChanged();
    }

    private _updateCurrentAttachmentPreviewModeListener = (previewMode: boolean): void => {
        this._state.isOnlyAttachmentPreviewMode = previewMode;
        this.emitChanged();
    }
}
